import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CardHeader, LinearProgress, Stack, TextField } from '@mui/material';
import { TuneOutlined } from '@mui/icons-material';
import api from '../../services/api';
import { ResearchLimits } from '../../types/research';

const fields: [keyof ResearchLimits, string, number, number, string][] = [
  ['max_ai_steps', 'AI analysis steps', 2, 100, 'Maximum gateway requests per run.'],
  ['max_tool_calls', 'Tool calls', 5, 200, 'Maximum Kali tool invocations per run.'],
  ['max_runtime_minutes', 'Research time (minutes)', 2, 60, 'Starts after Kali is ready.'],
  ['startup_timeout_minutes', 'Startup time (minutes)', 2, 45, 'Maximum time to provision and prepare Kali.'],
  ['tool_timeout_seconds', 'Per-tool timeout (seconds)', 5, 180, 'Stops a tool and its child processes at this limit.'],
  ['max_output_tokens', 'Output tokens per AI step', 1000, 32000, 'Must also be supported by the selected gateway model.'],
  ['daily_jobs_per_tenant', 'Daily jobs per tenant', 1, 200, 'Rolling 24-hour submission limit.'],
  ['max_crawl_pages', 'Automatic crawl pages', 1, 100, 'Shared across all selected web attack types and targets; also consumes tool calls.'],
  ['max_crawl_depth', 'Automatic crawl depth', 0, 5, 'Link levels from submitted targets. Zero inspects submitted pages only.'],
];

export default function ResearchSettingsCard() {
  const [values, setValues] = useState<ResearchLimits | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  useEffect(() => { let alive = true; api.getResearchSettings().then(v => { if (alive) setValues(v); }).catch(() => { if (alive) setError('Research limits could not be loaded.'); }); return () => { alive = false; }; }, []);
  const valid = values && fields.every(([key, , min, max]) => Number.isInteger(values[key]) && values[key] >= min && values[key] <= max);
  const save = async () => {
    if (!values) return;
    setBusy(true); setSaved(false); setError('');
    try { setValues(await api.updateResearchSettings(values)); setSaved(true); }
    catch { setError('Research limits could not be saved. Check the values and retry.'); }
    finally { setBusy(false); }
  };
  return <Card id="research-limits" variant="outlined"><CardHeader avatar={<TuneOutlined color="primary" />} title="Research limits"
    subheader="Global defaults for new research runs. Existing runs retain their original limits." />
    <CardContent><Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      {saved && <Alert severity="success">Research limits saved. They apply to the next run.</Alert>}
      {!values && !error && <LinearProgress aria-label="Loading research limits" />}
      {values && <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
        {fields.map(([key, label, min, max, help]) => <TextField key={key} type="number" label={label} value={values[key]} disabled={busy}
          inputProps={{ min, max, step: 1 }} helperText={`${help} Allowed: ${min}–${max}.`}
          error={!Number.isInteger(values[key]) || values[key] < min || values[key] > max}
          onChange={e => { setSaved(false); setValues({ ...values, [key]: Number(e.target.value) }); }} />)}
      </Box>}
      <Alert severity="info">These limits control research work and runtime. Gateway rate limits, provider quotas, and cloud charges are separate.</Alert>
      <Button onClick={save} disabled={!valid || busy} variant="contained" sx={{ alignSelf: 'flex-start' }}>{busy ? 'Saving…' : 'Save research limits'}</Button>
    </Stack></CardContent>
  </Card>;
}
